// Copyright 2025, Imprivata, Inc.  All rights reserved.

import { findIdpConfigSAML, saveIdpConfigSAML } from '@api/setupServices';
import { ErrorCode } from '@errorHandler/constants';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { AppError } from '@errorHandler/errors';
import { useMutation, useQuery } from '@tanstack/react-query';
import { STEPS } from '../constants';
import { useNavigateToNextStep } from './useNavigateToNextStep';

const useSaveIdpConfigSAML = (metadataId: string, idpConfigId?: string, isSetup = false) => {
  const navigateToNextStep = useNavigateToNextStep();
  const { emitError } = useErrorContext();

  return useMutation({
    mutationFn: async (values: { samlAttributeName: string; samlAttributeValue: string }) => {
      const requestData = {
        idpMetadataId: metadataId,
        idpConfigId: idpConfigId,
        samlGroupAttrName: values.samlAttributeName,
        samlGroupAttrValues: values.samlAttributeValue.split(',').map(item => item.trim()),
      };
      return saveIdpConfigSAML(requestData, isSetup);
    },
    onSuccess: data => {
      console.log('IdP Config saved:', data.idpConfigId);
      navigateToNextStep(STEPS.SETUP_COMPLETE);
    },
    onError: error => {
      emitError(new AppError({ code: ErrorCode.INCOMPLETE_OPERATION }));
      console.log('Error saving IdP SAML Config:', error);
    },
  });
};

const useFindIdpConfigSAML = (metadataId: string, tenantId: string) => {
  const {
    data: idpConfigsFindResponse,
    error,
    isLoading,
  } = useQuery({
    queryKey: ['idpConfigSAML', metadataId, tenantId],
    queryFn: () => findIdpConfigSAML({ idpMetadataId: metadataId }, tenantId),
    enabled: !!metadataId,
    retry: false,
  });

  return { samlConfig: idpConfigsFindResponse?.idpConfigs?.[0], error, isLoading };
};

export { useFindIdpConfigSAML, useSaveIdpConfigSAML };
